import { type EntityId } from './ecs';
import { type ElementData, type ElementType } from './element';

export type ToolName = string;

export type ToolCursorOverride = 'hand-tool-grabbing' | 'select-tool-duplicating';

export type ElementClipboardData<Type extends ElementType = any> = Omit<
  ElementData<Type>,
  'id'
>;

export interface CanvasDropPayload {
  type: ToolName;
  dropX: number;
  dropY: number;
}

export class CanvasDropEvent extends Event {
  detail: CanvasDropPayload;
  constructor(payload: CanvasDropPayload) {
    super('canvas:drop', { bubbles: true, cancelable: false });
    this.detail = payload;
  }
}

export interface CanvasToolPickPayload {
  type: ToolName;
}

export class CanvasToolPickEvent extends Event {
  detail: CanvasToolPickPayload;
  constructor(payload: CanvasToolPickPayload) {
    super('canvas:tool', { bubbles: true, cancelable: false });
    this.detail = payload;
  }
}

export class CanvasUIButtonEvent extends Event {
  detail: CanvasUIButtonEventPayload;
  constructor(payload: CanvasUIButtonEventPayload) {
    super('canvas:uiButtonEvent', { bubbles: true, cancelable: false });
    this.detail = payload;
  }
}

export interface CanvasUIButtonEventPayload {
  type: string;
  target: string;
}

export interface CanvasStampPickedPayload {
  id: string;
  height: number;
  width: number;
}

export class CanvasStampPickedEvent extends Event {
  detail: CanvasStampPickedPayload;
  constructor(payload: CanvasStampPickedPayload) {
    super('canvas:stamp', { bubbles: true, cancelable: false });
    this.detail = payload;
  }
}

export class CanvasCutEvent extends Event {
  constructor() {
    super('canvas:cut', { bubbles: true, cancelable: false });
  }
}

export class CanvasCopyEvent extends Event {
  constructor() {
    super('canvas:copy', { bubbles: true, cancelable: false });
  }
}

export class CanvasPasteEvent extends Event {
  constructor() {
    super('canvas:paste', { bubbles: true, cancelable: false });
  }
}

export class CanvasExportEvent extends Event {
  constructor() {
    super('canvas:export', { bubbles: true, cancelable: false });
  }
}

export class CanvasDeleteEvent extends Event {
  constructor() {
    super('canvas:delete', { bubbles: true, cancelable: false });
  }
}

export interface CanvasStickerPickedPayload {
  spriteId: string;
}

export class CanvasStickerPickedEvent extends Event {
  detail: CanvasStickerPickedPayload;
  constructor(payload: CanvasStickerPickedPayload) {
    super('canvas:stickerPicked', { bubbles: true, cancelable: false });
    this.detail = payload;
  }
}

export class CanvasImportEvent extends Event {
  constructor() {
    super('canvas:import', { bubbles: true, cancelable: false });
  }
}

/**
 * Emitted when a sidebar select button menu is opened. For instance, when
 * selecting an issue type
 */
export class SidebarSelectMenuOpenEvent extends Event {
  constructor() {
    super('canvas:sidebar-select:open', { bubbles: true, cancelable: false });
  }
}

/**
 * Emitted when a sidebar select button menu is closed
 */
export class SidebarSelectMenuCloseEvent extends Event {
  constructor() {
    super('canvas:sidebar-select:close', { bubbles: true, cancelable: false });
  }
}

export interface CanvasCreateCommentEventPayload {
  worldPosition: [number, number];
  parentEntityId?: EntityId;
}

/**
 * Fires when user clicks UI element to add a comment
 */
export class CanvasCreateCommentEvent extends Event {
  detail: CanvasCreateCommentEventPayload;
  constructor(detail: CanvasCreateCommentEventPayload) {
    super('canvas:create-comment', { bubbles: true, cancelable: false });
    this.detail = detail;
  }
}

/**
 * States to determine if an entity can be fillable/filled.
 * Fillable meaning can this entity be filled
 * Filled meaning if this entity is currently filled in or not
 */
export type FillProperties = {
  fillable: boolean;
  filled: boolean;
};
